import React, { Component } from 'react';
import TextField from '@material-ui/core/TextField';
import Collapse from '@material-ui/core/Collapse';


class Step5ReviewSave extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedSection: null,
      nameTouched: false
    };
  }

  handleNameChange = (event) => {
    this.props.onUpdate({ dictionaryName: event.target.value });
  }

  // Empty-on-blur validation: the "required"/duplicate errors only surface once
  // the user has focused into and out of the name field (issue #610).
  handleNameBlur = () => {
    this.setState({ nameTouched: true });
  }

  handleDescriptionChange = (event) => {
    this.props.onUpdate({ dictionaryDescription: event.target.value });
  }

  toggleSection = (section) => {
    this.setState(prevState => ({
      expandedSection: prevState.expandedSection === section ? null : section
    }));
  }

  getSourceName = () => {
    return this.props.selectedConnection?.name || 'Database Connection';
  }

  getCategoryLabel = (type) => {
    const labels = {
      tables: 'Tables',
      views: 'Views',
      storedProcedures: 'Stored Procedures',
      functions: 'Functions',
      triggers: 'Triggers'
    };
    return labels[type] || type;
  }

  getItemsByStatus = () => {
    const { selectedItems, businessContextData, schemaMetadata } = this.props;
    const fullyDocumented = [];
    const notDocumented = [];

    ['tables', 'views', 'storedProcedures'].forEach(type => {
      const selected = selectedItems[type] || [];
      const allOfType = schemaMetadata?.[type] || [];
      
      allOfType.forEach(item => {
        if (selected.includes(item.id)) {
          const context = businessContextData[item.id];
          const itemWithType = { ...item, itemType: type };
          
          if (context && context.businessContext && context.businessContext.trim()) {
            fullyDocumented.push(itemWithType);
          } else {
            notDocumented.push(itemWithType);
          }
        }
      });
    });

    return { fullyDocumented, notDocumented };
  }

  render() {
    const {
      dictionaryName,
      dictionaryDescription,
      completionStats,
      selectedItems,
      selectedAttributes,
      existingNames = [],
      error
    } = this.props;
    const { expandedSection, nameTouched } = this.state;

    // Name validation only shows after the field has been touched (blurred).
    const trimmedName = (dictionaryName || '').trim();
    const nameIsEmpty = !trimmedName;
    // Normalize here too so the check is self-contained regardless of caller.
    const nameIsDuplicate = !nameIsEmpty &&
      existingNames.map((n) => (n || '').trim().toLowerCase()).includes(trimmedName.toLowerCase());
    const showRequiredError = nameTouched && nameIsEmpty;
    const showDuplicateError = nameTouched && nameIsDuplicate;

    // Dynamically calculate totals for all selected item types
    const itemCounts = {};
    let totalItems = 0;
    
    if (selectedItems) {
      Object.keys(selectedItems).forEach(type => {
        const count = selectedItems[type]?.length || 0;
        if (count > 0) {
          itemCounts[type] = count;
          totalItems += count;
        }
      });
    }

    const { fullyDocumented, notDocumented } = this.getItemsByStatus();

    return (
      <div className="step5-container">
        <div className="dictionary-info-row">
          <div className="dictionary-info">
            <TextField
              label="Dictionary Name"
              value={dictionaryName}
              onChange={this.handleNameChange}
              onBlur={this.handleNameBlur}
              inputProps={{ 'data-testid': 'dd-name-input' }}
              fullWidth
              required
              error={showRequiredError || showDuplicateError}
              helperText={
                showRequiredError
                  ? "Dictionary name is required"
                  : showDuplicateError
                    ? "A dictionary with this name already exists"
                    : ""
              }
              style={{ marginBottom: 16 }}
            />

            <TextField
              label="Description (Optional)"
              value={dictionaryDescription}
              onChange={this.handleDescriptionChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Add a description for this data dictionary..."
              inputProps={{'data-testid': 'dd-description-input'}}
            />
          </div>

          <div className="documentation-status-subtle">
            <div className="status-label">Documentation Status</div>
            <div className="status-value">{completionStats.completionPercentage}%</div>
          </div>
        </div>

        <div className="summary-section">
          <h3>Summary & What's Included</h3>
          
          <div className="summary-stats-grid">
            <div className="summary-stat-card">
              <div className="stat-label">Source</div>
              <div className="stat-value">{this.getSourceName()}</div>
            </div>

            <div className="summary-stat-card">
              <div className="stat-label">Total Items</div>
              <div className="stat-value">{totalItems}</div>
            </div>

            {Object.keys(itemCounts).map(type => (
              <div key={type} className="summary-stat-card">
                <div className="stat-label">{this.getCategoryLabel(type)}</div>
                <div className="stat-value">{itemCounts[type]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Not Documented Items */}
        {notDocumented.length > 0 && (
          <div className="incomplete-items-section">
            <div className="status-group">
              <div 
                className="status-group-header empty"
                onClick={() => this.toggleSection('empty')}
              >
                <span className="material-icons">
                  {expandedSection === 'empty' ? 'expand_more' : 'chevron_right'}
                </span>
                <span className="status-icon">○</span>
                <span className="status-label">Not Documented</span>
                <span className="status-count">{notDocumented.length} items</span>
              </div>
              <Collapse in={expandedSection === 'empty'}>
                <div className="status-items">
                  {notDocumented.map(item => (
                    <div key={item.id} className="status-item">
                      <span className="material-icons">table_chart</span>
                      <span>{item.name}</span>
                      <span className="item-type-badge">{item.itemType}</span>
                    </div>
                  ))}
                </div>
              </Collapse>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span className="material-icons">error</span>
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
}

export default Step5ReviewSave;
